export const PROTOCOL_VERSION = "0.1";
export class CodexError extends Error {
    code;
    data;
    constructor(code, message, data) {
        super(message);
        this.code = code;
        this.data = data;
        this.name = "CodexError";
    }
}
export function isCodexError(value) {
    return value instanceof CodexError || Boolean(value && typeof value === "object" && "code" in value && "message" in value);
}
export async function getCodexProvider(options = {}) {
    if (typeof window === "undefined")
        throw new CodexError("PROVIDER_UNAVAILABLE", "window.codex requires a browser");
    if (window.codex)
        return window.codex;
    const timeoutMs = options.timeoutMs ?? 1500;
    return new Promise((resolve, reject) => {
        const done = () => {
            window.removeEventListener("codex#initialized", done);
            clearTimeout(timer);
            if (window.codex)
                resolve(window.codex);
            else
                reject(new CodexError("PROVIDER_UNAVAILABLE", "The Codex provider did not initialize"));
        };
        const timer = window.setTimeout(() => {
            window.removeEventListener("codex#initialized", done);
            reject(new CodexError("PROVIDER_UNAVAILABLE", "Install or enable the Codemask extension"));
        }, timeoutMs);
        window.addEventListener("codex#initialized", done, { once: true });
    });
}
export async function requestCodex(method, params, options) {
    const provider = await getCodexProvider();
    return provider.request({ method, params }, options);
}
export function createMockCodexProvider() {
    const listeners = new Map();
    const now = Math.floor(Date.now() / 1000);
    const grants = [];
    const tasks = new Map();
    const threads = Array.from({ length: 8 }, (_, index) => ({ id: `mock-thread-${index + 1}`, title: ["Ship the onboarding flow", "Debug Cloudflare deployment", "Refactor the provider contract", "Build a dashboard", "Tighten API permissions", "Fix CI failures", "Review the extension UX", "Create release notes"][index], createdAt: now - (index + 3) * 86400, updatedAt: now - index * 7200, turnCount: 3 + index, workspaceLabel: index % 2 ? "window.codex" : "product" }));
    const emit = (event, payload) => listeners.get(event)?.forEach(listener => listener(payload));
    const provider = {
        protocolVersion: PROTOCOL_VERSION,
        async request({ method, params }) {
            if (method === "provider.info")
                return { name: "Codemask", protocolVersion: PROTOCOL_VERSION, providerVersion: "mock", connected: grants.length > 0, profile: grants.length ? { type: "chatgpt", email: "developer@example.com", planType: "plus" } : null };
            if (method === "capabilities.list")
                return { methods: ["provider.info", "capabilities.list", "connect", "disconnect", "permissions.get", "permissions.request", "permissions.revoke", "workspace.select", "threads.list", "threads.analyze", "tasks.start", "tasks.get", "tasks.send", "tasks.cancel"], recipes: ["reflection.v1"] };
            if (method === "connect") {
                const grant = { id: crypto.randomUUID(), origin: location.origin, scopes: params.scopes, persistence: "persistent", createdAt: now };
                grants.push(grant);
                emit("provider.connected", { origin: location.origin });
                return { connected: true, grants };
            }
            if (method === "disconnect") {
                grants.length = 0;
                emit("provider.disconnected", { reason: "Mock disconnected" });
                return { disconnected: true };
            }
            if (method === "permissions.get")
                return { grants };
            if (method === "permissions.request") {
                const grant = { id: crypto.randomUUID(), origin: location.origin, scopes: params.scopes, persistence: params.persistence, createdAt: now };
                grants.push(grant);
                emit("permissions.changed", { grants });
                return { grant };
            }
            if (method === "permissions.revoke") {
                grants.splice(0, grants.length, ...grants.filter(grant => params.grantId && grant.id !== params.grantId));
                return { revoked: true };
            }
            if (method === "workspace.select")
                return { id: "mock-workspace", label: "my-project" };
            if (method === "threads.list")
                return { data: threads.slice(0, params.limit ?? 20), nextCursor: null };
            if (method === "threads.analyze")
                return { coverage: { selectedThreads: params.threadIds.length, analyzedThreads: params.threadIds.length, truncatedThreads: 0, from: new Date((now - 30 * 86400) * 1000).toISOString(), to: new Date().toISOString() }, activity: { threadCount: params.threadIds.length, turnCount: params.threadIds.length * 6, activeDays: Math.min(12, params.threadIds.length * 2) }, themes: [{ label: "Workflow automation", count: Math.max(2, params.threadIds.length - 1), threadIds: params.threadIds.slice(0, 3) }, { label: "Deployment confidence", count: 3, threadIds: params.threadIds.slice(1, 4) }, { label: "Clearer contracts", count: 2, threadIds: params.threadIds.slice(0, 2) }], frictions: [{ label: "Repeated environment setup", description: "Several tasks repeat the same local setup and deployment checks instead of reusing a verified workflow.", threadIds: params.threadIds.slice(0, 3) }, { label: "Late permission decisions", description: "Security boundaries are often settled after implementation has started, creating avoidable rework.", threadIds: params.threadIds.slice(1, 4) }], suggestions: [{ id: "workflow", title: "Create a reusable ship workflow", rationale: "A single verified workflow can remove repeated setup and validation work.", proposedPrompt: "Create a reusable project skill that handles setup, validation, and Cloudflare deployment for this repository. Document its safety boundaries and add a smoke test." }, { id: "contract", title: "Make permission checks executable", rationale: "Contract tests will keep the public provider boundary stable as the runtime changes.", proposedPrompt: "Add a provider conformance suite covering origin isolation, one-use grants, task confirmation, event filtering, and unknown method rejection." }] };
            if (method === "tasks.start") {
                const task = { id: crypto.randomUUID(), status: "running", title: params.title ?? "Mock Codex task", workspaceLabel: "my-project", createdAt: now, updatedAt: now, lastMessage: "Codex is preparing the workspace…" };
                tasks.set(task.id, task);
                setTimeout(() => emit("task.event", { taskId: task.id, sequence: 1, kind: "agentMessage", text: "I found the existing project structure and am adding the reusable workflow." }), 700);
                return task;
            }
            if (method === "tasks.get") {
                const task = tasks.get(params.taskId);
                if (!task)
                    throw new CodexError("TASK_NOT_FOUND", "Mock task not found");
                return task;
            }
            if (method === "tasks.send") {
                const task = tasks.get(params.taskId);
                if (!task)
                    throw new CodexError("TASK_NOT_FOUND", "Mock task not found");
                task.lastMessage = params.message;
                return task;
            }
            if (method === "tasks.cancel") {
                const task = tasks.get(params.taskId);
                if (!task)
                    throw new CodexError("TASK_NOT_FOUND", "Mock task not found");
                task.status = "cancelled";
                emit("task.cancelled", { task });
                return task;
            }
            throw new CodexError("UNSUPPORTED_METHOD", `Unsupported mock method: ${method}`);
        },
        on(event, listener) { const current = listeners.get(event) ?? new Set(); current.add(listener); listeners.set(event, current); },
        removeListener(event, listener) { listeners.get(event)?.delete(listener); }
    };
    return provider;
}
